import { useState, useEffect, lazy } from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { CookieConsent } from "@/components/CookieConsent";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { GenderProvider } from "@/contexts/GenderContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SkipNavigation } from "@/components/SkipNavigation";
import { AddToHomeScreenPrompt } from "@/components/AddToHomeScreenPrompt";
import { useAddToHomeScreenPrompt } from "@/hooks/useAddToHomeScreenPrompt";
import { useAuth } from "@/hooks/useAuth";
import Home from "@/pages/home";
import Stories from "@/pages/stories";
import Story from "@/pages/story";
import About from "@/pages/about";
import AuthPage from "@/pages/auth-page";
import Profile from "@/pages/profile";
import SubscribePage from "@/pages/subscribe";
import Marketplace from "@/pages/marketplace";
import Admin from "@/pages/admin";
import TermsOfService from "@/pages/terms";
import PrivacyPolicy from "@/pages/privacy";
import Error403 from "@/pages/error-403";
import Error500 from "@/pages/error-500";
import NotFound from "@/pages/not-found";
import Community from "@/pages/community";
import ForgotPassword from "@/pages/forgot-password";
import ResetPassword from "@/pages/reset-password";
import GiftSubscription from "@/pages/gift-subscription";
import RedeemPage from "@/pages/redeem";
import LandingPage from "@/pages/landing";
import FeaturesPage from "@/pages/features";
import PricingPage from "@/pages/pricing";
import HelpPage from "@/pages/help";
import Article from "@/pages/help/Article";
import { AdminReports } from "@/pages/AdminReports";
import AdminMerchandise from "@/pages/AdminMerchandise";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminPricingIntelligence from "@/pages/admin/AdminPricingIntelligence";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminUserManagement from "@/pages/admin/AdminUserManagement";
import AdminAIChat from "@/pages/admin/AdminAIChat";
import AdminStripeSync from "@/pages/admin/AdminStripeSync";
import AdminBadges from "@/pages/admin/AdminBadges";
import AdminInfluencers from "@/pages/admin/AdminInfluencers";
import AdminEmailTemplates from "@/pages/admin/AdminEmailTemplates";
import AdminEmailSequences from "@/pages/admin/AdminEmailSequences";
import DemoNonSubscriberPage from "@/pages/demo-non-subscriber";
import SnuggleCraftersInfluencerProgram from "@/pages/snugglecrafters-influencer-program";
import DeveloperPortal from "@/pages/developer-portal";
import DeveloperSignup from "@/pages/developer-signup";
import DeveloperDashboard from "@/pages/developer-dashboard";
import ApiDocs from "@/pages/api-docs";
import ProgramsPage from "@/pages/programs";
import AffiliateSignupPage from "@/pages/affiliate-signup";
import AffiliateInfoPage from "@/pages/affiliate-info";
import ContactPage from "@/pages/contact";
import PartnershipsContactPage from "@/pages/partnerships-contact";
import CharacterStudio from "@/pages/CharacterStudio";
import Characters from "@/pages/characters";
import InfluencerDashboard from "@/pages/influencer/InfluencerDashboard";
import InfluencerApplication from "@/pages/influencer/InfluencerApplication";
import InfluencerResources from "@/pages/influencer/InfluencerResources";
import Playlists from "@/pages/playlists";
import Playlist from "@/pages/playlist";
import SharedPlaylist from "@/pages/shared-playlist";
import ChildProfiles from "@/pages/child-profiles";
import ChildProfileEdit from "@/pages/child-profile-edit";
import OnboardingPage from "@/pages/onboarding";
import BuyGiftCardPage from "@/pages/buy-gift-card";
import BlackFridayPage from "@/pages/black-friday";
import CyberMondayPage from "@/pages/cyber-monday";
import SubscriptionStore from "@/pages/subscription-store";
import InfluencerJobPosting from "@/pages/jobs/influencer";
import ParentsPage from "@/pages/parents";
import BlogPage from "@/pages/blog";
import AppPage from "@/pages/app";
import BedtimeStoriesPage from "@/pages/stories/bedtime";
import EducationalStoriesPage from "@/pages/stories/educational";
import Stories3to5Page from "@/pages/stories/age-3-5";
import Stories6to8Page from "@/pages/stories/age-6-8";
// import { VRStoryPage } from "@/features/vr-story"; // Temporarily disabled - VR dependencies causing build issues

function Router() {
  return (
    <Switch>
      <Route path="/" component={LandingPage} />
      <ProtectedRoute path="/onboarding" component={OnboardingPage} />
      <ProtectedRoute path="/home" component={Home} />
      <ProtectedRoute path="/stories" component={Stories} />
      <ProtectedRoute path="/story/:id" component={Story} />
      {/* <ProtectedRoute path="/vr/story/:id" component={VRStoryPage} /> */}
      <Route path="/playlists/shared/:slug" component={SharedPlaylist} />
      <ProtectedRoute path="/playlists/:id" component={Playlist} />
      <ProtectedRoute path="/playlists" component={Playlists} />
      <ProtectedRoute path="/profile" component={Profile} />
      <ProtectedRoute path="/child-profiles/:id" component={ChildProfileEdit} />
      <ProtectedRoute path="/child-profiles" component={ChildProfiles} />
      <ProtectedRoute path="/character-studio" component={CharacterStudio} />
      <ProtectedRoute path="/characters" component={Characters} />
      <ProtectedRoute path="/influencer/dashboard" component={InfluencerDashboard} />
      <ProtectedRoute path="/influencer/application" component={InfluencerApplication} />
      <ProtectedRoute path="/influencer/apply" component={InfluencerApplication} />
      <ProtectedRoute path="/influencer/resources" component={InfluencerResources} />
      <ProtectedRoute path="/influencer" component={InfluencerDashboard} />
      <ProtectedRoute path="/store" component={SubscriptionStore} />
      <ProtectedRoute path="/marketplace" component={Marketplace} />
      <ProtectedRoute path="/admin" component={Admin} />
      <ProtectedRoute path="/admin/cms" component={AdminCMS} />
      <ProtectedRoute path="/admin/dashboard" component={AdminDashboard} />
      <ProtectedRoute path="/admin/payments" component={AdminPayments} />
      <ProtectedRoute path="/admin/reports" component={AdminReports} />
      <ProtectedRoute path="/admin/merchandise" component={AdminMerchandise} />
      <ProtectedRoute path="/admin/pricing-intelligence" component={AdminPricingIntelligence} />
      <ProtectedRoute path="/admin/support" component={AdminSupport} />
      <ProtectedRoute path="/admin/users" component={AdminUserManagement} />
      <ProtectedRoute path="/admin/badges" component={AdminBadges} />
      <ProtectedRoute path="/admin/stripe-sync" component={AdminStripeSync} />
      <ProtectedRoute path="/admin/ai-chat" component={AdminAIChat} />
      <ProtectedRoute path="/admin/influencers" component={AdminInfluencers} />
      <ProtectedRoute path="/admin/email-templates" component={AdminEmailTemplates} />
      <ProtectedRoute path="/admin/email-sequences" component={AdminEmailSequences} />
      <Route path="/community" component={Community} />
      <Route path="/auth" component={AuthPage} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/subscribe" component={SubscribePage} />
      <Route path="/gift-subscription" component={GiftSubscription} />
      <Route path="/buy-gift-card" component={BuyGiftCardPage} />
      <Route path="/redeem" component={RedeemPage} />
      <Route path="/about" component={About} />
      <Route path="/features" component={FeaturesPage} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/programs" component={ProgramsPage} />
      <Route path="/help" component={HelpPage} />
      <Route path="/help/:category/:article" component={Article} />
      <Route path="/parents" component={ParentsPage} />
      <Route path="/blog" component={BlogPage} />
      <Route path="/app" component={AppPage} />
      <Route path="/stories/bedtime" component={BedtimeStoriesPage} />
      <Route path="/stories/educational" component={EducationalStoriesPage} />
      <Route path="/stories/age-3-5" component={Stories3to5Page} />
      <Route path="/stories/age-6-8" component={Stories6to8Page} />
      <Route path="/affiliate/signup" component={AffiliateSignupPage} />
      <Route path="/affiliate/info" component={AffiliateInfoPage} />
      <Route path="/contact" component={ContactPage} />
      <Route path="/partnerships/contact" component={PartnershipsContactPage} />
      <Route path="/snugglecrafters-influencer-program" component={SnuggleCraftersInfluencerProgram} />
      <Route path="/jobs/influencer" component={InfluencerJobPosting} />
      <Route path="/developer" component={DeveloperPortal} />
      <Route path="/developer-portal" component={DeveloperPortal} />
      <Route path="/developer/dashboard" component={DeveloperDashboard} />
      <Route path="/developer-signup" component={DeveloperSignup} />
      <Route path="/api-docs" component={ApiDocs} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/error/403" component={Error403} />
      <Route path="/error/500" component={Error500} />
      <Route path="/demo-non-subscriber" component={DemoNonSubscriberPage} />
      <Route path="/black-friday" component={BlackFridayPage} />
      <Route path="/cyber-monday" component={CyberMondayPage} />
      <Route component={NotFound} />
    </Switch>
  );
}


function App() {
  const { showPrompt, handleClose, handleDontShowAgain, options } = useAddToHomeScreenPrompt();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SkipNavigation />
        <AuthProvider>
          <GenderProvider>
            <TooltipProvider>
              <Toaster />
              <CookieConsent />
              <Router />
              <AddToHomeScreenPrompt
                isOpen={showPrompt}
                onClose={handleClose}
                onDontShowAgain={handleDontShowAgain}
                options={options}
              />
            </TooltipProvider>
          </GenderProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;